import { ImageName } from "@mcleod/images";
import {
    Button, Component, ComponentTypes, Container, DataHeader,
    DataHeaderProps, DataSourceMode, Panel, PanelProps, SaveButton
} from "@mcleod/components";
import { CompanySettings, getThemeColor } from "@mcleod/core";
import { CollapsingHeader } from "./CollapsingHeader";
import { CollapseController } from "./CollapseController";
import { AppSaveButton } from "../savebutton/AppSaveButton";
import { AppHeaderActions, AppHeaderActionPanels } from "./AppHeaderActions";

export class AppDataHeader extends DataHeader implements CollapsingHeader, AppHeaderActionPanels {
    private _initialHeight: number;
    private _isTitleCollapsed: boolean;
    private _appHeaderActions: AppHeaderActions;

    constructor(props?: Partial<DataHeaderProps>) {
        super(props);
    }

    protected override get initComponents(): Component[] {
        return [this.panelTitle, this.addlPanelBottom];
    }

    public override get panelTitle(): Panel {
        const panel = super.panelTitle;
        panel.setProps({ fillRow: true, rowBreak: true, paddingTop: 12, paddingBottom: 12 });
        return panel;
    }

    public get defaultProps(): Partial<PanelProps> {
        if (this._designer == null)
            return { fillRow: true, marginBottom: 0, padding: 0 };
        else
            return super.defaultProps;
    }

    protected override addTool(tool: Component) {
        if (tool === this.closeButton) {
            CollapseController.syncNavigationLeft();
        } else if (tool === this.searchButton) {
            this.manageSearchButton();
        } else if (tool === this.saveButton) {
            this.manageSaveButton();
        } else {
            super.addTool(tool);
        }
    }

    protected override syncTools(mode: DataSourceMode) {
        super.syncTools(mode);
        CollapseController.syncNavigation();
    }

    private manageSearchButton() {
        this.searchButton.setProps({
            imageColor: CompanySettings.get().text_color ?? getThemeColor("primary.reverse")
        });
        CollapseController.syncNavigationRight();
    }

    private manageSaveButton() {
        if (this.saveButton instanceof AppSaveButton) {
            this.saveButton.enabledButtonColor = CompanySettings.get().text_color  ?? getThemeColor("primary.reverse");
            this.saveButton.doAfterEnabledPropChange = this.doOnSaveButtonEnabledPropChange;
        }
    }

    private doOnSaveButtonEnabledPropChange(): void {
        CollapseController.syncNavigationRight();
    }

    protected override get closeButton(): Button {
        const button = super.closeButton;
        button.imageProps = {
            name: ImageName.x,
            height: 28,
            width: 28,
            color: CompanySettings.get().text_color ?? getThemeColor("primary.reverse")
        }
        return button;
    }

    override get parent(): Container {
        return super.parent;
    }

    override set parent(value: Container) {
        super.parent = value;
        if (this._designer == null) {
            CollapseController.pushCollapsingHeader(this);
        }
    }

    get isTitleCollapsed(): boolean {
        return this._isTitleCollapsed ?? false;
    }

    set isTitleCollapsed(value: boolean) {
        this._isTitleCollapsed = value;
    }

    get initialHeight(): number {
        return this._initialHeight;
    }

    set initialHeight(value: number) {
        this._initialHeight = value;
    }

    get lhsNavigationButton(): Button | null {
        if (this.showClose)
            return this.closeButton;
        return null;
    }

    get rhsActionButton(): Button | SaveButton | null {
        switch (this.dataSource?.mode) {
            case DataSourceMode.SEARCH:
                return this.searchButton;
            case DataSourceMode.ADD:
            case DataSourceMode.UPDATE:
                if (this.saveButton.enabled)
                    return this.saveButton;
            // Intentional fall-through to show menu if save disabled.
            case DataSourceMode.NONE:
                if (this.appHeaderActions.hasActions)
                    return this.appHeaderActions.contextButton;
            // Intentional fall-through to show nothing if no menu actions.
            default:
                return null;
        }
    }

    private get appHeaderActions(): AppHeaderActions {
        if (this._appHeaderActions == null) {
            this._appHeaderActions = new AppHeaderActions(this);
        }
        return this._appHeaderActions;
    }

    public addAppAction(button: Button): void {
        this.appHeaderActions.addAppAction(button);
    }
}

ComponentTypes.registerAppComponentType("dataheader", {
    constructor: AppDataHeader.prototype.constructor
});
